(function() {
  if (window.customElements.get("collection-infinite-scroll")) {
    return;
  }

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.productList = document.getElementById(this.dataset.productListId);
      this.paginationElement = this.querySelector("[data-infinite-scroll-pagination]");
      this.statusElement = this.querySelector("[data-infinite-scroll-status]");
      this.progressElement = this.querySelector("[data-infinite-scroll-progress]");
      this.nextLink = this.querySelector("[data-infinite-scroll-next]");
      this.isLoading = false;
      this.isDisabled = false;

      this.hydrateFallbackDataset();
      this.ensureProgressControls();
      this.normalizeControlOrder();
      this.updateNextLinkLabel();
      this.updateProgressFromDataset();

      if (!this.productList || !this.nextLink) {
        return;
      }

      this.addEventListener("click", this.onClick, { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    onClick = (event) => {
      const nextLink = event.target instanceof Element ? event.target.closest("[data-infinite-scroll-next]") : null;

      if (!nextLink || !this.contains(nextLink) || this.isDisabled) {
        return;
      }

      event.preventDefault();
      this.loadNextPage();
    };

    async loadNextPage() {
      if (this.isLoading || !this.nextLink || this.isDisabled) {
        return;
      }

      this.isLoading = true;
      this.setAttribute("aria-busy", "true");
      this.nextLink.setAttribute("aria-disabled", "true");

      const nextUrl = new URL(this.nextLink.href, window.location.origin);

      if (this.dataset.sectionId) {
        nextUrl.searchParams.set("section_id", this.dataset.sectionId);
      }

      try {
        const response = await fetch(nextUrl.toString(), {
          credentials: "same-origin",
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
        const newProductList = responseDocument.getElementById(this.dataset.productListId);
        const newController = responseDocument.querySelector(`collection-infinite-scroll[data-product-list-id="${this.dataset.productListId}"]`);

        if (!newProductList) {
          throw new Error("Could not find the next product list.");
        }

        this.appendProducts(newProductList);
        this.updateControls(newController, responseDocument, newProductList);
      } catch (error) {
        if (error.name !== "AbortError") {
          this.allowNormalPagination();
        }
      } finally {
        if (!this.abortController.signal.aborted) {
          this.isLoading = false;
          this.removeAttribute("aria-busy");
          this.nextLink?.removeAttribute("aria-disabled");
        }
      }
    }

    appendProducts(newProductList) {
      const fragment = document.createDocumentFragment();

      Array.from(newProductList.children).forEach((productCard) => {
        fragment.appendChild(document.importNode(productCard, true));
      });

      this.productList.appendChild(fragment);
    }

    updateControls(newController, responseDocument, newProductList) {
      if (!newController) {
        const legacyNextLink = this.findLegacyNextLink(responseDocument, newProductList);

        if (legacyNextLink) {
          this.dataset.loadedCount = this.countLoadedProducts().toString();
          this.replacePaginationWithNextLink(legacyNextLink);
          this.updateProgressFromDataset();
        } else {
          this.paginationElement?.replaceChildren();
          this.nextLink = null;
        }

        return;
      }

      Object.entries(newController.dataset).forEach(([key, value]) => {
        this.dataset[key] = value;
      });

      const newStatusElement = newController.querySelector("[data-infinite-scroll-status]");
      if (this.statusElement && newStatusElement) {
        this.statusElement.textContent = newStatusElement.textContent;
      }

      const newProgressElement = newController.querySelector("[data-infinite-scroll-progress]");
      if (this.progressElement && newProgressElement) {
        this.progressElement.style.setProperty("--progress", newProgressElement.style.getPropertyValue("--progress"));
        this.progressElement.setAttribute("aria-valuenow", newProgressElement.getAttribute("aria-valuenow") || this.dataset.loadedCount);
        this.progressElement.setAttribute("aria-valuemax", newProgressElement.getAttribute("aria-valuemax") || this.dataset.totalCount);
        this.progressElement.setAttribute("aria-label", newProgressElement.getAttribute("aria-label") || this.statusElement?.textContent || "");
      }

      const newPaginationElement = newController.querySelector("[data-infinite-scroll-pagination]");
      if (this.paginationElement && newPaginationElement) {
        this.paginationElement.replaceChildren(...document.importNode(newPaginationElement, true).childNodes);
      }

      this.nextLink = this.querySelector("[data-infinite-scroll-next]");
      this.ensureProgressControls();
      this.normalizeControlOrder();
      this.updateNextLinkLabel();
      this.updateProgressFromDataset();
    }

    findLegacyNextLink(responseDocument, newProductList) {
      const legacyPaginationRoot = newProductList?.closest(".collection__main") || responseDocument;

      return legacyPaginationRoot.querySelector('.pagination a[rel="next"]');
    }

    replacePaginationWithNextLink(sourceLink) {
      if (!this.paginationElement) {
        return;
      }

      this.paginationElement.replaceChildren(this.buildNextLink(sourceLink));
      this.nextLink = this.querySelector("[data-infinite-scroll-next]");
    }

    buildNextLink(sourceLink) {
      const nextLink = document.createElement("a");
      nextLink.className = "button button--outline collection-infinite-scroll__link";
      nextLink.rel = "next";
      nextLink.href = sourceLink.href;
      nextLink.textContent = this.getLoadMoreLabel(sourceLink);
      nextLink.dataset.infiniteScrollNext = "";
      nextLink.setAttribute("aria-controls", this.dataset.productListId);
      nextLink.setAttribute("aria-label", nextLink.textContent);

      return nextLink;
    }

    hydrateFallbackDataset() {
      const collectionMain = this.closest(".collection__main");

      this.dataset.loadedCount ||= collectionMain?.dataset.collectionLoadedCount || this.countLoadedProducts().toString();
      this.dataset.totalCount ||= collectionMain?.dataset.collectionTotalCount || CollectionInfiniteScroll.inferTotalCount(this).toString();
      this.dataset.loadMoreLabel ||= CollectionInfiniteScroll.defaultLoadMoreLabel(this.nextLink);
      this.dataset.statusTemplate ||= CollectionInfiniteScroll.defaultStatusTemplate(this.nextLink || this);
    }

    countLoadedProducts() {
      if (!this.productList) {
        return 0;
      }

      return this.productList.querySelectorAll("product-card").length || this.productList.children.length;
    }

    ensureProgressControls() {
      if (!this.paginationElement) {
        return;
      }

      if (!this.statusElement) {
        const statusWrapper = document.createElement("p");
        statusWrapper.className = "collection-infinite-scroll__status";
        statusWrapper.setAttribute("role", "status");
        statusWrapper.setAttribute("aria-live", "polite");
        statusWrapper.setAttribute("aria-atomic", "true");

        const statusText = document.createElement("span");
        statusText.dataset.infiniteScrollStatus = "";
        statusWrapper.append(statusText);
        this.paginationElement.after(statusWrapper);
        this.statusElement = statusText;
      }

      if (!this.progressElement) {
        const progressElement = document.createElement("div");
        progressElement.className = "collection-infinite-scroll__progress";
        progressElement.setAttribute("role", "progressbar");
        progressElement.setAttribute("aria-valuemin", "0");
        progressElement.dataset.infiniteScrollProgress = "";
        this.statusElement.closest(".collection-infinite-scroll__status")?.after(progressElement);
        this.progressElement = progressElement;
      }
    }

    normalizeControlOrder() {
      if (!this.paginationElement || !this.statusElement || !this.progressElement) {
        return;
      }

      const statusWrapper = this.statusElement.closest(".collection-infinite-scroll__status") || this.statusElement;
      const referenceNode = this.querySelector("noscript");

      statusWrapper.hidden = false;
      statusWrapper.classList.remove("sr-only");
      this.progressElement.classList.remove("sr-only");

      [this.paginationElement, statusWrapper, this.progressElement].forEach((element) => {
        if (referenceNode) {
          this.insertBefore(element, referenceNode);
        } else {
          this.append(element);
        }
      });
    }

    updateNextLinkLabel() {
      if (!this.nextLink) {
        return;
      }

      const label = this.getLoadMoreLabel(this.nextLink);
      this.nextLink.textContent = label;
      this.nextLink.setAttribute("aria-label", label);
    }

    getLoadMoreLabel(sourceElement) {
      return this.dataset.loadMoreLabel || CollectionInfiniteScroll.defaultLoadMoreLabel(sourceElement);
    }

    updateProgressFromDataset() {
      const renderedProductCount = this.countLoadedProducts();

      if (renderedProductCount > 0) {
        this.dataset.loadedCount = renderedProductCount.toString();
      }

      const loadedCount = Number.parseInt(this.dataset.loadedCount, 10);
      const totalCount = Number.parseInt(this.dataset.totalCount, 10);

      if (!Number.isFinite(loadedCount)) {
        return;
      }

      const hasTotalCount = Number.isFinite(totalCount) && totalCount > 0;
      const statusText = this.getStatusText(loadedCount, hasTotalCount ? totalCount : null);

      if (this.statusElement) {
        this.statusElement.textContent = statusText;
      }

      if (this.progressElement && hasTotalCount) {
        this.progressElement.hidden = false;
        this.progressElement.style.setProperty("--progress", Math.min(loadedCount / totalCount, 1).toString());
        this.progressElement.setAttribute("aria-valuenow", loadedCount.toString());
        this.progressElement.setAttribute("aria-valuemax", totalCount.toString());
        this.progressElement.setAttribute("aria-label", statusText);
      } else if (this.progressElement) {
        this.progressElement.hidden = true;
      }
    }

    getStatusText(loadedCount, totalCount) {
      if (totalCount) {
        const statusTemplate = this.dataset.statusTemplate || CollectionInfiniteScroll.defaultStatusTemplate(this);

        return statusTemplate.replace("[loaded_count]", loadedCount).replace("[total_count]", totalCount);
      }

      return CollectionInfiniteScroll.defaultStatusWithoutTotalTemplate(this).replace("[loaded_count]", loadedCount);
    }

    allowNormalPagination() {
      this.isDisabled = true;
      this.removeAttribute("aria-busy");
      this.nextLink?.removeAttribute("aria-disabled");
    }

    static upgradeLegacyPaginations(root = document) {
      root.querySelectorAll(".collection__main").forEach((collectionMain) => {
        if (collectionMain.querySelector("collection-infinite-scroll")) {
          return;
        }

        const productList = collectionMain.querySelector("product-list[id]");
        const paginationElement = collectionMain.querySelector(".pagination");
        const nextLink = paginationElement?.querySelector('a[rel="next"]');

        if (!productList || !paginationElement || !nextLink) {
          return;
        }

        const sectionElement = collectionMain.closest('.shopify-section[id^="shopify-section-"]');
        const controller = document.createElement("collection-infinite-scroll");
        controller.className = "collection-infinite-scroll";
        controller.dataset.productListId = productList.id;
        controller.dataset.loadedCount = (productList.querySelectorAll("product-card").length || productList.children.length).toString();
        controller.dataset.totalCount = CollectionInfiniteScroll.inferTotalCount(collectionMain).toString();
        controller.dataset.loadMoreLabel = CollectionInfiniteScroll.defaultLoadMoreLabel(nextLink);
        controller.dataset.statusTemplate = CollectionInfiniteScroll.defaultStatusTemplate(nextLink);

        if (sectionElement) {
          controller.dataset.sectionId = sectionElement.id.replace("shopify-section-", "");
        }

        const pagination = document.createElement("nav");
        pagination.className = "collection-infinite-scroll__pagination";
        pagination.setAttribute("role", "navigation");
        pagination.setAttribute("aria-label", paginationElement.getAttribute("aria-label") || "Pagination");
        pagination.dataset.infiniteScrollPagination = "";

        controller.paginationElement = pagination;
        pagination.append(controller.buildNextLink(nextLink));
        controller.replaceChildren(pagination, controller.buildStatusElement(), controller.buildProgressElement());
        paginationElement.replaceWith(controller);
      });
    }

    buildStatusElement() {
      const statusWrapper = document.createElement("p");
      statusWrapper.className = "collection-infinite-scroll__status";
      statusWrapper.setAttribute("role", "status");
      statusWrapper.setAttribute("aria-live", "polite");
      statusWrapper.setAttribute("aria-atomic", "true");

      const statusText = document.createElement("span");
      statusText.dataset.infiniteScrollStatus = "";
      statusWrapper.append(statusText);

      return statusWrapper;
    }

    buildProgressElement() {
      const progressElement = document.createElement("div");
      progressElement.className = "collection-infinite-scroll__progress";
      progressElement.setAttribute("role", "progressbar");
      progressElement.setAttribute("aria-valuemin", "0");
      progressElement.dataset.infiniteScrollProgress = "";

      return progressElement;
    }

    static defaultLoadMoreLabel(sourceElement) {
      return CollectionInfiniteScroll.isItalian(sourceElement) ? "Mostrare altri prodotti" : "Load more products";
    }

    static defaultStatusTemplate(sourceElement) {
      return CollectionInfiniteScroll.isItalian(sourceElement)
        ? "Hai visto [loaded_count] prodotti di [total_count]"
        : "You have seen [loaded_count] products out of [total_count]";
    }

    static defaultStatusWithoutTotalTemplate(sourceElement) {
      return CollectionInfiniteScroll.isItalian(sourceElement)
        ? "Hai visto [loaded_count] prodotti"
        : "You have seen [loaded_count] products";
    }

    static inferTotalCount(root) {
      const collectionMain = root.closest(".collection__main");
      const datasetTotalCount = collectionMain?.dataset.collectionTotalCount;

      if (datasetTotalCount) {
        return Number.parseInt(datasetTotalCount, 10) || 0;
      }

      const scope = root.closest(".shopify-section") || document;
      const totalCountElement = scope.querySelector(".collection-toolbar__products-count");
      const totalCount = totalCountElement?.textContent.match(/\d[\d\s.,]*/)?.[0]?.replace(/\D/g, "");

      return Number.parseInt(totalCount, 10) || 0;
    }

    static isItalian(sourceElement) {
      return document.documentElement.lang?.toLowerCase().startsWith("it")
        || window.location.pathname.startsWith("/it/")
        || /\b(successivo|avanti|prodotti?)\b/i.test(sourceElement?.textContent || "");
    }
  }

  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);

  const upgradeLegacyPaginations = (root = document) => {
    CollectionInfiniteScroll.upgradeLegacyPaginations(root);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => upgradeLegacyPaginations(), { once: true });
  } else {
    upgradeLegacyPaginations();
  }

  document.addEventListener("shopify:section:load", (event) => upgradeLegacyPaginations(event.target));
})();
